import React, {FC, useState} from "react"
import {Body, Label, H3, H4, H5, H6} from "@irati/ui/src/Typography"
import {Hr, Col, Row} from "@irati/ui/src/Layout"
import FormattedMessage, {FormattedCurrency} from "@irati/utils/lang"
import {UserSavingsBrand} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import PropTypes from "prop-types"
import {TextButton} from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Spacing from "@irati/ui/src/Spacing"
import mySavingsTracking from "@irati-tracking/my-savings/mySavings"
import {BrandLogo, DealRow, StyledHr, Header, TableRow, TypeIcon, DealHeaderWrapper, OtherRedemptionsIconWrapper} from "./styles"
import {getTypeIcon} from "./TypeIcons"

interface Props {
  brands?: UserSavingsBrand[]
  currency: string
}

const RedemptionTable: FC<Props> = ({brands, currency}, {apps}) => {
  if (!brands?.length) return null
  if (brands?.length === 1 && brands[0].brandId === "no-brand") return null
  const [expandedRowIndex, setExpandedRowIndex] = useState()
  const theme = useTheme()

  const handleToggle = (rowIndex) => {
    setExpandedRowIndex(expandedRowIndex === rowIndex ? null : rowIndex)
  }

  return (
    <>
      <H3 marginBottom={1}>
        <FormattedMessage id="savings.redemptionTable.title" />
      </H3>
      <StyledHr flush />
      <Row paddingTop={1} paddingBottom={1}>
        <Col xs={8}>
          <H6>
            <FormattedMessage id="savings.redemptionTable.deals.title" />
          </H6>
        </Col>
        <Col xs={4}>
          <H6 ellipsis>
            <FormattedMessage id="platform.mySavings.userTable.headers.savings" />
          </H6>
        </Col>
      </Row>
      <StyledHr flush />

      {brands?.map((item, i) => (
        <>
          <TableRow key={`table-${i}`}>
            <Col sm={4} xs={8}>
              <Flex alignItems="center" gap={2.5}>
                {item?.brandId === "no-brand" ? (
                  <OtherRedemptionsIconWrapper>
                    <Icon icon={IconTypes.perks} size={20} />
                  </OtherRedemptionsIconWrapper>
                ) : (
                  <BrandLogo logoUrl={item?.brandLogoUrl} />
                )}
                <Flex direction="column" flex="1">
                  {item.brandName && (
                    <H5>
                      {item.brandSlug ? (
                        <ButtonRouterLink
                          to={`/brands/${item.brandSlug}`}
                          app={apps.PERKS}
                          cta
                          tertiary
                          onClick={() => mySavingsTracking.clickBrand("my_savings", "perks", "redemption_table")}>
                          <Header>{item.brandName}</Header>
                        </ButtonRouterLink>
                      ) : (
                        item.brandName
                      )}
                    </H5>
                  )}
                  <Label>
                    <FormattedMessage id="savings.redemptionTable.brand.redemption.deals" values={{count: item.deals?.length}} />
                  </Label>
                </Flex>
              </Flex>
            </Col>
            <Col xsHidden sm={4}>
              <Flex alignItems="center">
                <Body small ellipsis>
                  <FormattedMessage id="savings.redemptionTable.brand.redemption.number" values={{count: item.totalRedemptions}} />
                </Body>
              </Flex>
            </Col>
            <Col sm={4} xs={4}>
              <Flex justifyContent="space-between">
                <H4>
                  <FormattedCurrency currency={currency} value={item.totalSavings} isCents={false} />
                </H4>
                <Flex>
                  <TextButton onClick={() => handleToggle(i)}>
                    <Flex alignItems="center" gap={0.5}>
                      <Spacing hideOnMobile>
                        <H6 color={theme.colors.primary2}>
                          <FormattedMessage id="savings.redemptionTable.breakdown" />
                        </H6>
                      </Spacing>
                      <Icon
                        icon={expandedRowIndex === i ? IconTypes.chevronUp : IconTypes.chevronDown}
                        size={10}
                        color={theme.colors.primary2}
                      />
                    </Flex>
                  </TextButton>
                </Flex>
              </Flex>
            </Col>
          </TableRow>
          <Hr flush />

          {expandedRowIndex === i && (
            <>
              {item.deals?.map((deal) => (
                <DealRow>
                  <Col xs={9} sm={4}>
                    <Flex gap={0.5} direction="column">
                      <Flex alignItems="center">
                        <TypeIcon>{getTypeIcon(deal.type)}</TypeIcon>
                        <DealHeaderWrapper small>
                          <ButtonRouterLink
                            full
                            app={deal.type === "reward" ? apps.FLEXIREWARDS : apps.PERKS}
                            to={`/info/${deal.urlSlug}`}
                            cta
                            tertiary
                            onClick={() => mySavingsTracking.clickPerk("my_savings", "redemption_table")}>
                            <Header>{deal.header}</Header>
                          </ButtonRouterLink>
                        </DealHeaderWrapper>
                      </Flex>
                      <Spacing hideOnDesktop marginLeft={3}>
                        <Label>
                          <FormattedMessage id="savings.redemptionTable.brand.redemption.number" values={{count: deal.redemptions}} />
                        </Label>
                      </Spacing>
                    </Flex>
                  </Col>
                  <Col xsHidden sm={4}>
                    <Label>
                      <FormattedMessage id="savings.redemptionTable.brand.redemption.number" values={{count: deal.redemptions}} />
                    </Label>
                  </Col>
                  <Col xs={3} sm={4}>
                    <Label>
                      <FormattedCurrency currency={currency} value={deal.totalSavings?.value} isCents={false} />
                    </Label>
                  </Col>
                </DealRow>
              ))}
              <Hr flush />
            </>
          )}
        </>
      ))}
    </>
  )
}

RedemptionTable.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default RedemptionTable
